// @givmo/board — the running to-do list, on every Board page (CBG-UX7-WEB-DIR, CBG-UX7-WEB-OFF;
// design stop design-268's §4, §7 and §8, frames d1–d5, o1–o4 and t1).
//
// Jonathan, 2026-10-22 11:57 PDT: "a minimizable popup list that can run on top of any page in Board
// on the right side of the screen - when you minimize it, it goes to the bottom right as a little tab
// labelled to-do list, and when you click it, it expands, and when you click on an item in it, it
// brings you to the place to accomplish that item". At 12:00 PDT: officers get theirs too, the phone
// is the designer's call, and "maybe there can be a "completed" tab within the to-do list that
// completed items move to".
//
// ══ THREE FORMS OF ONE LIST (§7) ═══════════════════════════════════════════════════════════════════
//
//   * WIDE (≥1280px): the open list stands on the right, from under the console's bar (the officer
//     frame's on a director's board page too, CBG-UX10) to the bottom edge, 360px wide, and the page
//     narrows beside it (`PageShell`, `DirectorShell`), so it never covers what the page needs. Choosing an item opens its page and the
//     list stays open.
//   * NARROWER (640–1279px): the same panel over the page's right side, non-modal; choosing an item
//     minimizes it, so the page it opened is fully in view.
//   * PHONE (<640px): a sheet that slides up over the page, the design system's own bottom `Sheet`, up
//     to 85% of the screen, the page dimmed; choosing an item closes it to the tab.
//   Minimized, it is a tab flush with the bottom edge at the right, "To-do list" and the count: the open
//   panel's own top folded down, so the two read as one thing. It steps aside under an open dialog
//   (it can't be used beneath the scrim, and would only cover the dimmed page's last row) and comes
//   back when the dialog closes.
//
// ══ WHAT IT IS TO ASSISTIVE TECH (§8) ══════════════════════════════════════════════════════════════
//
// Open on a wide or tablet screen, a named landmark (`aside`, "To-do list") after the page's own
// content, that leaves the page fully usable: no focus trap; Escape inside it minimizes it and the
// focus returns to the tab. The tab is a button whose name carries the count, with `aria-expanded` and
// `aria-controls`. On a phone the sheet is a modal dialog: focus held, Escape and its close button
// minimize it, and the focus returns to the tab. "To do" and "Completed" are a real tab list; every
// item is a real link (a keyboard, a new tab, its address copied), a step's "Mark done" a button, and
// the group labels headings. The count's change is announced politely, once; nothing else is.

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  Badge,
  Button,
  FOCUS_RING,
  Icon,
  IconButton,
  Sheet,
  Tabs,
  cn,
  testIdProp,
  type TabsValue,
} from '@givmo/design-system';
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  CircleCheck,
  CircleCheckBig,
  ListChecks,
} from 'lucide-react';
import { Link } from '../../board-routing';
import { HowThisWorks } from '../../components/fine-print';
import { ComingUpOpen } from '../director/coming-up-row';
import {
  countOf,
  type DoneItem,
  type ToDoItem,
  type ToDoList,
  type ToDoTarget,
} from './todo-model';
import { useToDoState, useToDoStore, type ToDoSurface, type ToDoTab } from './todo-store';
import { todoWords as w } from './todo-words';

const LINE = 'font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong';
const DETAIL = 'font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary';
const QUIET = 'font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary';
/** By when: the item's own quiet line, never coloured (a date is a fact, not an alarm). */
const DUE = 'font-sans text-s12 font-w500 leading-h15 tracking-givmo text-text-tertiary';
const GROUP =
  'px-16 pb-4 pt-14 font-sans text-s12 font-w600 uppercase tracking-wide text-text-tertiary';
const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-700 outline-none hover:underline focus-visible:underline';

/** The wide form's own breakpoint (Tailwind's `xl`), where the page narrows beside the list. */
const WIDE = '(min-width: 1280px)';
/** The phone's: below Tailwind's `sm`, the bottom sheet. */
const PHONE = '(max-width: 639.98px)';

function matches(query: string): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

/** Whether the viewport matches `query`, following it as it changes. */
function useMedia(query: string): boolean {
  const [matched, setMatched] = useState(() => matches(query));
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const list = window.matchMedia(query);
    const change = () => setMatched(list.matches);
    change();
    list.addEventListener('change', change);
    return () => list.removeEventListener('change', change);
  }, [query]);
  return matched;
}

/**
 * Whether a modal dialog is open on the page: the design system's dialogs and sheets mark themselves
 * `aria-modal`, as the console's phone drawer does.
 */
function useModalOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () =>
      setOpen(document.querySelector('[role="dialog"][aria-modal="true"]') !== null);
    check();
    const observer = new MutationObserver(check);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal', 'role'],
    });
    return () => observer.disconnect();
  }, []);
  return open;
}

/** The count, said once when it changes after the list's first answer (never on arrival). */
function useCountAnnouncement(count: number | null): string {
  const [said, setSaid] = useState('');
  const last = useRef<number | null>(null);
  useEffect(() => {
    if (count === null) return;
    if (last.current !== null && last.current !== count) setSaid(w.tabName(count));
    last.current = count;
  }, [count]);
  return said;
}

export interface ToDoPanelProps {
  surface: ToDoSurface;
  /** Whose list it is on this device (the store's memory); null while the host hasn't read it. */
  account: string | null;
  /** The list, or null while its reads haven't answered. */
  list: ToDoList | null;
  /** Every read the list needs failed: nothing to show but the way to try again. */
  failed?: boolean;
  /**
   * Completed's own read failed (the director's third): its tab says so, with Try again, and never
   * that nothing was completed. To do is unaffected.
   */
  completedFailed?: boolean;
  onRetry?: () => void;
  /** A step marked done from the list (the member's own act), and the one being marked. */
  onMarkDone?: (itemId: string) => void;
  marking?: string | null;
  /** CBG-FIX74's Open, and whether it is under way. */
  onOpenWaitingMinutes?: () => void;
  openingWaitingMinutes?: boolean;
  /** A line under the list (the officer's way to their own board page). */
  footer?: ReactNode;
  /** The member's own checklist page, linked under the checklist's items. */
  checklistPage?: ToDoTarget;
  /** To do's line when it holds nothing, where the surface's own isn't the seat's (a committee's). */
  emptyLine?: string;
  testId: string;
}

export function ToDoPanel({
  surface,
  account,
  list,
  failed = false,
  completedFailed = false,
  onRetry,
  onMarkDone,
  marking = null,
  onOpenWaitingMinutes,
  openingWaitingMinutes = false,
  footer,
  checklistPage,
  emptyLine,
  testId,
}: ToDoPanelProps) {
  const store = useToDoStore();
  const { open, tab } = useToDoState(surface);
  const phone = useMedia(PHONE);
  const modalOpen = useModalOpen();
  const tabRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const focusNext = useRef<'tab' | 'panel' | null>(null);
  const panelId = `${testId}-panel`;
  const items = list?.items ?? [];
  const count = list === null ? null : countOf(items);
  const said = useCountAnnouncement(count);
  const isOpen = open === true;

  // The focus follows the person's own act: into the panel when they open it, back to the tab when
  // they minimize it (a phone's sheet holds its own focus while open). The tab draws only once no
  // dialog is open, and on a phone the list's own sheet counts until it has left the page: the
  // request waits for the tab rather than going nowhere (review-274's F2).
  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    if (target === 'tab' && !isOpen) {
      if (tabRef.current === null) return;
      tabRef.current.focus();
    } else if (target === 'panel' && isOpen && !phone) {
      panelRef.current?.focus();
    }
    focusNext.current = null;
  }, [isOpen, phone, modalOpen]);

  const setOpen = (next: boolean, focus: 'tab' | 'panel' | null) => {
    focusNext.current = focus;
    store?.setOpen(surface, account, next);
  };
  const minimize = () => setOpen(false, 'tab');
  // Choosing from the list (an item's link, or FIX74's Open): below the wide form the list gets out
  // of the page's way (remembered), and what the choice brings counts as shown (the store's rule).
  const chose = () => {
    store?.chose(surface);
    if (!matches(WIDE)) setOpen(false, null);
  };

  const body = (
    <ToDoBody
      surface={surface}
      tab={tab}
      onTab={(next) => store?.setTab(surface, next)}
      items={items}
      completed={list?.completed ?? []}
      loading={list === null && !failed}
      failed={failed}
      completedFailed={completedFailed}
      onRetry={onRetry}
      onMarkDone={onMarkDone}
      marking={marking}
      onOpenWaitingMinutes={
        onOpenWaitingMinutes === undefined
          ? undefined
          : () => {
              chose();
              onOpenWaitingMinutes();
            }
      }
      openingWaitingMinutes={openingWaitingMinutes}
      onChoose={chose}
      count={count ?? 0}
      checklistPage={checklistPage}
      emptyLine={emptyLine}
      testId={testId}
    />
  );

  return (
    <>
      <span className="sr-only" aria-live="polite" {...testIdProp(`${testId}-live`)}>
        {said}
      </span>

      {!isOpen ? (
        modalOpen ? null : (
          <button
            ref={tabRef}
            type="button"
            aria-expanded={false}
            aria-controls={panelId}
            // Until the list has answered, its name claims no count: "nothing to do" isn't known.
            aria-label={count === null ? w.title : w.tabName(count)}
            onClick={() => setOpen(true, 'panel')}
            className={cn(
              'fixed bottom-0 right-16 z-30 flex h-44 items-center gap-8 rounded-t-lg border border-b-0 border-border bg-surface pl-14 pr-12 shadow-lg transition-colors hover:bg-surface-hover sm:right-24',
              FOCUS_RING,
            )}
            {...testIdProp(`${testId}-tab`)}
          >
            <Icon icon={ListChecks} size={18} className="text-brand-600" aria-hidden />
            <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong">
              {w.title}
            </span>
            {count !== null && count > 0 ? (
              <Badge tone="brand" size="sm" aria-hidden>
                {count}
              </Badge>
            ) : null}
            <Icon icon={ChevronUp} size={16} className="text-icon-muted" aria-hidden />
          </button>
        )
      ) : phone ? (
        <Sheet.Root
          open
          onOpenChange={(next) => {
            if (!next) minimize();
          }}
        >
          <Sheet.Content side="bottom" className="max-h-[85dvh]" testId={`${testId}-sheet`}>
            <div
              className="mx-auto mt-8 h-4 w-36 shrink-0 rounded-full bg-neutral-200"
              aria-hidden
            />
            <Sheet.Header title={w.title} className="border-b-0 pb-4 pt-10" />
            {body}
            {footer !== undefined ? (
              <div className="shrink-0 border-t border-border-subtle px-16 py-12">{footer}</div>
            ) : null}
          </Sheet.Content>
        </Sheet.Root>
      ) : (
        <aside
          ref={panelRef}
          id={panelId}
          aria-label={w.title}
          tabIndex={-1}
          onKeyDown={(event) => {
            // Only an Escape pressed in the panel itself: one inside a popover it opened (a portal,
            // whose events still bubble through React) closes that popover alone.
            if (event.key !== 'Escape' || event.defaultPrevented) return;
            if (!(event.target instanceof Node) || !panelRef.current?.contains(event.target))
              return;
            event.stopPropagation();
            minimize();
          }}
          className="fixed bottom-0 right-16 top-[72px] z-30 flex w-[360px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-t-xl border border-b-0 border-border bg-surface shadow-xl outline-none sm:right-24"
          {...testIdProp(panelId)}
        >
          <div className="flex shrink-0 items-center justify-between gap-12 px-16 pb-4 pt-12">
            <h2 className="flex items-center gap-8 font-sans text-s16 font-w700 tracking-givmo text-text-strong">
              <Icon icon={ListChecks} size={18} className="text-brand-600" aria-hidden />
              {w.title}
            </h2>
            <IconButton
              size="sm"
              aria-label={w.minimize}
              aria-expanded
              aria-controls={panelId}
              onClick={minimize}
              testId={`${testId}-minimize`}
            >
              <Icon icon={ChevronDown} size={18} aria-hidden />
            </IconButton>
          </div>
          {body}
          {footer !== undefined ? (
            <div className="shrink-0 border-t border-border-subtle px-16 py-12">{footer}</div>
          ) : null}
        </aside>
      )}
    </>
  );
}

interface BodyProps {
  surface: ToDoSurface;
  tab: ToDoTab;
  onTab: (tab: ToDoTab) => void;
  items: readonly ToDoItem[];
  completed: readonly DoneItem[];
  loading: boolean;
  failed: boolean;
  completedFailed: boolean;
  onRetry?: () => void;
  onMarkDone?: (itemId: string) => void;
  marking: string | null;
  onOpenWaitingMinutes?: () => void;
  openingWaitingMinutes: boolean;
  onChoose: () => void;
  count: number;
  checklistPage?: ToDoTarget;
  emptyLine?: string;
  testId: string;
}

function ToDoBody({
  surface,
  tab,
  onTab,
  items,
  completed,
  loading,
  failed,
  completedFailed,
  onRetry,
  onMarkDone,
  marking,
  onOpenWaitingMinutes,
  openingWaitingMinutes,
  onChoose,
  count,
  checklistPage,
  emptyLine,
  testId,
}: BodyProps) {
  return (
    <Tabs.Root
      appearance="saas"
      value={tab}
      onValueChange={(value: TabsValue) => onTab(value === 'completed' ? 'completed' : 'todo')}
      className="flex min-h-0 flex-1 flex-col"
      testId={`${testId}-tabs`}
    >
      <Tabs.List aria-label={w.title} className="shrink-0 px-16">
        <Tabs.Tab value="todo" testId={`${testId}-tab-todo`}>
          <span className="flex items-center gap-6">
            {w.tabToDo}
            {count > 0 ? (
              <Badge tone="brand" size="sm">
                {count}
              </Badge>
            ) : null}
          </span>
        </Tabs.Tab>
        <Tabs.Tab value="completed" testId={`${testId}-tab-completed`}>
          {w.tabCompleted}
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel
        value="todo"
        focusable={false}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-12"
      >
        {failed ? (
          <Failed onRetry={onRetry} testId={testId} />
        ) : loading ? (
          <Placeholders />
        ) : items.length === 0 ? (
          <Empty
            title={w.emptyTitle}
            line={emptyLine ?? (surface === 'director' ? w.emptyDirector : w.emptyOfficer)}
            testId={`${testId}-empty`}
          />
        ) : (
          <ToDoItems
            items={items}
            onMarkDone={onMarkDone}
            marking={marking}
            onOpenWaitingMinutes={onOpenWaitingMinutes}
            openingWaitingMinutes={openingWaitingMinutes}
            onChoose={onChoose}
            checklistPage={checklistPage}
            testId={testId}
          />
        )}
      </Tabs.Panel>
      <Tabs.Panel
        value="completed"
        focusable={false}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-12"
      >
        {failed ? (
          <Failed onRetry={onRetry} testId={testId} />
        ) : loading ? (
          <Placeholders />
        ) : completed.length === 0 && !completedFailed ? (
          <Empty title={w.emptyCompleted} testId={`${testId}-completed-empty`} />
        ) : (
          <>
            {/* Completed's own read failed: it says so, and what the other read knows still shows
                (§7), never under the line that says it is all of the last 90 days. */}
            {completedFailed ? (
              <Failed
                line={w.completedLoadFailed}
                onRetry={onRetry}
                testId={`${testId}-completed`}
              />
            ) : (
              <p className={cn(QUIET, 'px-16 pb-4 pt-12')}>{w.completedLine}</p>
            )}
            {completed.length === 0 ? null : (
              <ul className="flex flex-col">
                {completed.map((item) => (
                  <li key={item.key} className="border-t border-border-subtle first:border-t-0">
                    <Row
                      target={item.target}
                      onChoose={onChoose}
                      testId={`${testId}-done-${item.key}`}
                    >
                      <Icon
                        icon={CircleCheck}
                        size={18}
                        className="mt-[1px] shrink-0 text-icon-muted"
                        aria-hidden
                      />
                      <span className="flex min-w-0 flex-1 flex-col gap-2">
                        <span className={cn(LINE, 'font-w500 text-text-secondary')}>
                          {item.line}
                        </span>
                        {item.which ? <span className={DETAIL}>{item.which}</span> : null}
                        <span className={QUIET}>{item.detail}</span>
                      </span>
                    </Row>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Tabs.Panel>
    </Tabs.Root>
  );
}

function ToDoItems({
  items,
  onMarkDone,
  marking,
  onOpenWaitingMinutes,
  openingWaitingMinutes,
  onChoose,
  checklistPage,
  testId,
}: {
  items: readonly ToDoItem[];
  onMarkDone?: (itemId: string) => void;
  marking: string | null;
  onOpenWaitingMinutes?: () => void;
  openingWaitingMinutes: boolean;
  onChoose: () => void;
  checklistPage?: ToDoTarget;
  testId: string;
}) {
  const checklistId = useId();
  const waitingId = useId();
  const checklist = items.filter((item) => item.group === 'checklist');
  const waiting = items.filter((item) => item.group === 'waiting');
  const labelled = checklist.length > 0;
  const form990 = items.some((item) => item.form990 === true);
  const committeeMinutes = items.some((item) => item.committee === true);
  const list = (group: readonly ToDoItem[]) => (
    <ItemList
      items={group}
      onMarkDone={onMarkDone}
      marking={marking}
      onOpenWaitingMinutes={onOpenWaitingMinutes}
      openingWaitingMinutes={openingWaitingMinutes}
      onChoose={onChoose}
      testId={testId}
    />
  );
  return (
    <>
      {labelled ? (
        <section aria-labelledby={checklistId} {...testIdProp(`${testId}-checklist`)}>
          <h3 id={checklistId} className={GROUP}>
            {w.checklistGroup}
          </h3>
          <p className={cn(QUIET, 'px-16 pb-4')}>{w.checklistLine}</p>
          {list(checklist)}
          {checklistPage !== undefined ? (
            <div className="px-16 pb-6 pt-2">
              <Link
                to={checklistPage.to}
                {...(checklistPage.search === undefined ? {} : { search: checklistPage.search })}
                onClick={onChoose}
                className={LINK}
                data-testid={`${testId}-checklist-page`}
              >
                {w.openChecklist}
              </Link>
            </div>
          ) : null}
        </section>
      ) : null}
      {waiting.length > 0 ? (
        labelled ? (
          <section aria-labelledby={waitingId} {...testIdProp(`${testId}-waiting`)}>
            <h3 id={waitingId} className={GROUP}>
              {w.waitingGroup}
            </h3>
            {list(waiting)}
          </section>
        ) : (
          <div {...testIdProp(`${testId}-waiting`)}>{list(waiting)}</div>
        )
      ) : null}
      {form990 ? (
        <div className="px-16 pt-10">
          <HowThisWorks
            label={w.form990Link}
            lines={w.form990Lines(committeeMinutes)}
            className="text-s12"
            testId={`${testId}-form990-how`}
          />
        </div>
      ) : null}
    </>
  );
}

function ItemList({
  items,
  onMarkDone,
  marking,
  onOpenWaitingMinutes,
  openingWaitingMinutes,
  onChoose,
  testId,
}: {
  items: readonly ToDoItem[];
  onMarkDone?: (itemId: string) => void;
  marking: string | null;
  onOpenWaitingMinutes?: () => void;
  openingWaitingMinutes: boolean;
  onChoose: () => void;
  testId: string;
}) {
  return (
    <ul className="flex flex-col">
      {items.map((item) => {
        const words = (
          <span className="flex min-w-0 flex-1 flex-col gap-2">
            <span
              className={cn(
                LINE,
                item.act === undefined && 'transition-colors group-hover:text-brand-700',
              )}
            >
              {item.line}
            </span>
            {item.detail ? <span className={DETAIL}>{item.detail}</span> : null}
            {item.due ? <span className={DUE}>{item.due}</span> : null}
          </span>
        );
        const act = item.act;
        return (
          <li key={item.key} className="border-t border-border-subtle first:border-t-0">
            {act?.kind === 'mark-done' ? (
              <div
                className="flex items-center gap-12 px-16 py-12"
                {...testIdProp(`${testId}-item-${item.key}`)}
              >
                {words}
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  loading={marking === act.itemId}
                  onClick={() => onMarkDone?.(act.itemId)}
                  testId={`${testId}-mark-${item.key}`}
                >
                  {w.markDone}
                </Button>
              </div>
            ) : act?.kind === 'open-waiting-minutes' ? (
              <div
                className="flex items-center gap-12 px-16 py-12"
                {...testIdProp(`${testId}-item-${item.key}`)}
              >
                {words}
                {/* CBG-FIX74's Open, carried over from the retired card unchanged. */}
                <ComingUpOpen
                  loading={openingWaitingMinutes}
                  onClick={() => onOpenWaitingMinutes?.()}
                  testId={`${testId}-open-${item.key}`}
                />
              </div>
            ) : (
              <Row target={item.target} onChoose={onChoose} testId={`${testId}-item-${item.key}`}>
                {words}
                {item.target !== undefined ? (
                  <Icon
                    icon={ChevronRight}
                    size={16}
                    className="mt-2 shrink-0 text-icon-muted"
                    aria-hidden
                  />
                ) : null}
              </Row>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** One row: a real link to where it's done, or (with nowhere to go) the words alone. */
function Row({
  target,
  onChoose,
  testId,
  children,
}: {
  target: ToDoTarget | undefined;
  onChoose: () => void;
  testId: string;
  children: ReactNode;
}) {
  const className = 'group flex items-start gap-12 px-16 py-12';
  if (target === undefined) {
    return (
      <div className={className} {...testIdProp(testId)}>
        {children}
      </div>
    );
  }
  return (
    <Link
      to={target.to}
      {...(target.params === undefined ? {} : { params: target.params })}
      {...(target.search === undefined ? {} : { search: target.search })}
      onClick={onChoose}
      className={cn(
        className,
        'outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover',
      )}
      data-testid={testId}
    >
      {children}
    </Link>
  );
}

function Placeholders() {
  return (
    <div className="flex flex-col gap-10 px-16 py-16" aria-hidden>
      {[0, 1, 2].map((row) => (
        <div key={row} className="h-36 animate-pulse rounded-md bg-surface-sunken" />
      ))}
    </div>
  );
}

function Failed({
  line = w.loadFailed,
  onRetry,
  testId,
}: {
  line?: string;
  onRetry?: () => void;
  testId: string;
}) {
  return (
    <div
      className="flex flex-col items-start gap-10 px-16 py-16"
      {...testIdProp(`${testId}-failed`)}
    >
      <p className={DETAIL}>{line}</p>
      <Button
        appearance="saas"
        tone="secondary"
        size="sm"
        onClick={onRetry}
        testId={`${testId}-retry`}
      >
        {w.tryAgain}
      </Button>
    </div>
  );
}

function Empty({ title, line, testId }: { title: string; line?: string; testId: string }) {
  return (
    <div className="flex flex-col items-start gap-6 px-16 py-20" {...testIdProp(testId)}>
      <Icon icon={CircleCheckBig} size={20} className="text-icon-muted" aria-hidden />
      <p className={LINE}>{title}</p>
      {line !== undefined ? <p className={DETAIL}>{line}</p> : null}
    </div>
  );
}
